import React, { useState, FC, KeyboardEvent, useEffect } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import StaffModal from '../common/staffDetail';

interface Media {
  desktop_media?: { url: string; alternativeText?: string }[];
}

interface TeamMember {
  name: string;
  designation: string;
  profileLink?: string;
  media?: Media;
  teamMember: any;
}

interface SectionsOurTeamProps {
  data: {
    __typename: string;
    heading: string;
    profile_btn_label?: string;
    team: TeamMember[];
  };
}

const ComponentSectionsOurTeam: FC<SectionsOurTeamProps> = ({ data }: { data: any }) => {
  const sectionsOurTeam = data;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null);
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');

  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);
  const handleMouseEnter = (index: number) => () => setHoveredIndex(index);
  const handleMouseLeave = () => setHoveredIndex(null);

  // Optional: handle keyboard for accessibility
  const handleKeyDown = (index: number) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setHoveredIndex(index === hoveredIndex ? null : index);
    }
  };

  return (
    <section
      id={sectionsOurTeam.__typename}
      className="pb-10 md:pb-24 relative border-t border-[#B6C38066] pt-4 ourteam"
      style={{
        paddingTop: `${top}rem`,
        paddingBottom: `${bottom}rem`,
      }}
    >
      <h3 className="text-[20px] md:text-[22px] text-[#E5E1D5] font-light leading-none mb-7 lg:mb-5">
        {sectionsOurTeam.heading}
      </h3>
      <table className="w-full text-base leading-9 font-light relative">
        <tbody>
          {sectionsOurTeam.team.map((member: TeamMember, index: number) => {
            const hasImage = !!member.media?.desktop_media?.[0]?.url;
            return (
              <tr
                key={member.name + index}
                className="border-b border-[#b6c38066] text-[#E5E1D5] relative cursor-pointer hover:border-[#B6C380] transition-all hover:text-[#B6C380] hover-effecttr"
                onMouseEnter={handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                onFocus={handleMouseEnter(index)}
                onBlur={handleMouseLeave}
                tabIndex={0}
                onKeyDown={handleKeyDown(index)}
                onClick={() => setSelectedTeamMember(member)}
              >
                <td className="relative w-2/4">
                  {member.name}

                  <AnimatePresence>
                    {hoveredIndex === index && hasImage && (
                      <motion.div
                        key="hover-image"
                        className="absolute top-0/4 left-2/3 md:left-2/4 translate-y-[-50%] z-10 rounded-none"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                      >
                        <Image
                          src={member.media!.desktop_media![0].url}
                          alt={member.media!.desktop_media![0].alternativeText || member.name}
                          width={167}
                          height={289}
                          unoptimized={false}
                          priority={false}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </td>
                <td>{member.designation}</td>
                <td className="text-right">
                  <button
                    onClick={() => setSelectedTeamMember(member)}
                    className="profile-btn inline-flex items-center gap-2"
                    rel="noopener noreferrer"
                  >
                    {sectionsOurTeam.profile_btn_label || 'Profile'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <AnimatePresence>
        {selectedTeamMember && (
          <StaffModal teamMember={selectedTeamMember} onClose={() => setSelectedTeamMember(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};

export default ComponentSectionsOurTeam;
